import { Mutation, JsonApiRequestError } from '@farfetched/core';
import { chain } from '@leprecoins/effector';
import { sample, Store } from 'effector';
import {
	UuidResponse,
	WalletWithAccountId,
	WithdrawWalletMutationParams,
	createWithdrawWalletMutation,
} from 'shared/api';
import { createModalFormModel, ModalFormModel, notEmptyRule, notify } from 'shared/lib';

export type WithdrawWalletFormValue = {
	amount: string;
};

export type WithdrawWalletModel = ModalFormModel<WalletWithAccountId, WithdrawWalletFormValue> & {
	withdrawWalletMutation: Mutation<WithdrawWalletMutationParams, UuidResponse, JsonApiRequestError>;
	$pending: Store<boolean>;
};

export const createWithdrawWalletModel = (): WithdrawWalletModel => {
	const withdrawWalletMutation = createWithdrawWalletMutation();

	const model = createModalFormModel<WalletWithAccountId, WithdrawWalletFormValue>({
		fields: {
			amount: {
				init: '',
				rules: [notEmptyRule],
			},
		},
		resetErrorsOnChange: true,
		validateOn: ['submit'],
	});

	const { $pending } = withdrawWalletMutation;

	sample({
		clock: model.form.submitted,
		source: {
			amount: model.form.fields.amount.$value,
			wallet: model.$modalData,
		},
		filter: ({ wallet }) => wallet !== null,
		fn: ({ amount, wallet }) => ({
			amount: Number(amount),
			currency: wallet!.currency,
			accountId: wallet!.accountId,
		}),
		target: withdrawWalletMutation.start,
	});

	chain(withdrawWalletMutation.finished.success, model.switcher.disable);

	notify(withdrawWalletMutation.finished.success, 'withdraw-wallet.success');

	chain(model.modalClosed, withdrawWalletMutation.reset);

	return {
		withdrawWalletMutation,
		$pending,
		...model,
	};
};
